// Righe del carrello raggruppate per zona (redesign 2026-10).
// Solo presentazione: calcoli, validazione e salvataggio restano in Cart.js / CartContext.
// - zone come blocchi (intestazione colorata, richiudibile, rinomina, "+ Riga in zona")
// - codice e descrizione nella stessa colonna, modificabili in linea
// - quantità con − / +, listino e sconto modificabili, netto e totale calcolati
// - menu ⋯: nota riga, duplica, sposta su/giù (nella zona), sposta in zona, elimina
import React, { useEffect, useRef, useState } from 'react';
import { groupByZone, listZones, normalizeZone, sameZone } from './cartZones';
import '../css/CartLines.css';
import { useItemImage, useCatalogEditable } from './cartImages';

const MAX_ZONE_CHARS = 100;

const RowMenu = ({ children, label }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  return (
    <div className="cl-menu" ref={ref}>
      <button type="button" className="cl-menu-btn" aria-label={label} aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        ⋯
      </button>
      {open && (
        <div className="cl-menu-pop" role="menu" onClick={(e) => { if (e.target.closest('button')) setOpen(false); }}>
          {children}
        </div>
      )}
    </div>
  );
};

// Righe create dal configuratore "Profili su misura": si possono riaprire con "Modifica".
// (anche i prodotti del Catalogo con varianti, vedi useCatalogEditable)
export const isEditableProfile = (item) => item?.datasheetType === 'profileDynamic' || !!item?.datasheetConfig?.modelName;

const CartLines = ({
  items,                 // righe del carrello (array del CartContext)
  zoneColorOf,
  canEditDiscount,
  getRowDiscount,
  getNetUnit,
  getRowTotal,
  fmtEur,
  fieldErrors,
  onUpdate,              // (id, field, value)
  onUpdateNotes,         // (id, notes)
  onQuantity,            // (id, value)
  onPrice,               // (id, value)
  onDiscount,            // (id, value)
  onMove,                // (id, direction)
  onMoveZone,            // (zona, direction) sposta l'intera zona
  onRemove,              // (id)
  onAddRow,              // (zone)
  onDuplicate,           // (item)
  onEdit,                // (item) riapre la riga nel configuratore (Profili su misura) o nel Catalogo (varianti)
  highlightId,           // riga appena aggiornata (evidenziata un attimo)
  onAddAdjustment,
  requestZoneName,       // (callback(nome)) apre il modulo "Nuova zona"
  t,
}) => {
  const ordered = groupByZone(items);
  const zones = listZones(items);
  const hasZones = zones.length > 0;
  const [collapsed, setCollapsed] = useState({});
  const [filter, setFilter] = useState('');
  const [noteOpen, setNoteOpen] = useState({});
  const [renaming, setRenaming] = useState(null);
  const [zoom, setZoom] = useState(null);
  const imageOf = useItemImage();
  const isCatalogEditable = useCatalogEditable();

  useEffect(() => {
    if (!zoom) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setZoom(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [zoom]);

  // Errori di validazione: Cart.js li indicizza sulla posizione nell'array del carrello.
  const errorsOf = (item) => {
    const idx = items.indexOf(item);
    return {
      code: fieldErrors[`itemCode${idx}`],
      description: fieldErrors[`itemDescription${idx}`],
      price: fieldErrors[`itemUnitPrice${idx}`],
      quantity: fieldErrors[`itemQuantity${idx}`],
    };
  };

  // Gruppi consecutivi per zona (ordered è già raggruppato).
  const groups = [];
  ordered.forEach((item) => {
    const last = groups[groups.length - 1];
    if (last && sameZone(last.items[0], item)) last.items.push(item);
    else groups.push({ zone: normalizeZone(item.zone), items: [item] });
  });

  const q = filter.trim().toLowerCase();
  const matches = (item) => !q || `${item.code || ''} ${item.description || ''} ${item.notes || ''}`.toLowerCase().includes(q);

  // Nome zona chiesto con il modulo della pagina (CartDialog) tramite Cart.js.
  const askZoneName = (onName) =>
    requestZoneName((name) => {
      const clean = normalizeZone(name).slice(0, MAX_ZONE_CHARS);
      if (clean) onName(clean);
    });

  // Rinomina TUTTE le righe della zona (raggruppate senza badare a maiuscole/minuscole):
  // prima cambiava solo quelle scritte identiche alla prima e le altre diventavano una zona a parte.
  const renameZone = (group, to) => {
    const next = normalizeZone(to).slice(0, MAX_ZONE_CHARS);
    setRenaming(null);
    if (!next) return;
    group.items.filter((it) => normalizeZone(it.zone) !== next).forEach((it) => onUpdate(it.id, 'zone', next));
  };

  let rowNumber = 0;

  return (
    <div className="cl">
      <div className="cl-toolbar">
        <div className="cl-toolbar-group">
          {canEditDiscount && (
            <>
              <button type="button" className="cl-btn" onClick={() => onAddRow('')}>+ {t('cart.addRow')}</button>
              <button
                type="button"
                className="cl-btn"
                onClick={() => askZoneName((name) => onAddRow(name))}
              >
                + {t('cart.newZone')}
              </button>
              <button type="button" className="cl-btn" onClick={onAddAdjustment}>+ {t('cart.addAdjustment')}</button>
            </>
          )}
        </div>
        <input
          type="search"
          className="cl-search"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('cart.searchRows')}
          aria-label={t('cart.searchRows')}
        />
      </div>

      <div className="cl-cols" aria-hidden="true">
        <span>#</span>
        <span />
        <span>{t('cart.item')}</span>
        <span className="cl-num">{t('cart.quantity')}</span>
        <span className="cl-num">{t('cart.listPrice')}</span>
        <span className="cl-num">Sc%</span>
        <span className="cl-num">{t('cart.netPrice')}</span>
        <span className="cl-num">{t('cart.total')}</span>
        <span />
      </div>

      {!items.length && <div className="cl-empty">{t('cart.empty')}</div>}

      {groups.map((group, groupIndex) => {
        const color = zoneColorOf(group.zone);
        const key = group.zone.toLowerCase() || '__none__';
        const isCollapsed = !!collapsed[key];
        const visible = group.items.filter(matches);
        const zoneStyle = color
          ? { '--cl-zbg': color.bg, '--cl-zac': color.accent }
          : { '--cl-zbg': 'var(--cl-soft)', '--cl-zac': 'var(--cl-line)' };
        return (
          <section key={key} className={`cl-zone${isCollapsed ? ' is-collapsed' : ''}`} style={zoneStyle}>
            {/* Intestazione: solo se nel carrello ci sono zone (senza zone il carrello è una lista semplice) */}
            {hasZones && (
              <div className="cl-zone-head">
                <button
                  type="button"
                  className="cl-chev"
                  aria-label={isCollapsed ? t('cart.expandZone') : t('cart.collapseZone')}
                  onClick={() => setCollapsed((c) => ({ ...c, [key]: !c[key] }))}
                >
                  ▾
                </button>
                {renaming === key ? (
                  <input
                    className="cl-zone-rename"
                    autoFocus
                    defaultValue={group.zone}
                    maxLength={MAX_ZONE_CHARS}
                    aria-label={t('cart.renameZone')}
                    onBlur={(e) => renameZone(group, e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') e.currentTarget.blur();
                      if (e.key === 'Escape') setRenaming(null);
                    }}
                  />
                ) : (
                  <span className="cl-zone-name" title={group.zone || t('cart.noZone')}>{group.zone || t('cart.noZone')}</span>
                )}
                <span className="cl-zone-count">
                  {group.items.length} {group.items.length === 1 ? t('cart.rowSingular') : t('cart.rowPlural')}
                </span>
                <span className="cl-spacer" />
                {onMoveZone && groups.length > 1 && renaming !== key && (
                  <span className="cl-zone-move">
                    <button type="button" disabled={groupIndex === 0} onClick={() => onMoveZone(group.zone, -1)} aria-label={t('cart.moveZoneUp')} title={t('cart.moveZoneUp')}>▲</button>
                    <button type="button" disabled={groupIndex === groups.length - 1} onClick={() => onMoveZone(group.zone, 1)} aria-label={t('cart.moveZoneDown')} title={t('cart.moveZoneDown')}>▼</button>
                  </span>
                )}
                {group.zone && renaming !== key && (
                  <button type="button" className="cl-link" onClick={() => setRenaming(key)}>{t('cart.renameZone')}</button>
                )}
              </div>
            )}

            {!isCollapsed && (
              <div className="cl-rows">
                {visible.map((item) => {
                  rowNumber += 1;
                  const isAdj = item.type === 'adjustment';
                  const err = errorsOf(item);
                  const pos = ordered.indexOf(item);
                  const canUp = pos > 0 && sameZone(item, ordered[pos - 1]);
                  const canDown = pos < ordered.length - 1 && sameZone(item, ordered[pos + 1]);
                  const showNote = !!item.notes || noteOpen[item.id];
                  const otherZones = zones.filter((z) => z.toLowerCase() !== normalizeZone(item.zone).toLowerCase());
                  return (
                    <div key={item.id} className={`cl-row${isAdj ? ' is-adj' : ''}${highlightId != null && item.id === highlightId ? ' is-flash' : ''}`}>
                      <span className="cl-idx cl-num">{rowNumber}</span>
                      {(() => {
                        const src = imageOf(item);
                        if (!src) {
                          return <span className="cl-thumb is-empty" aria-hidden="true">{isAdj ? '−€' : ''}</span>;
                        }
                        return (
                          <button
                            type="button"
                            className="cl-thumb"
                            title={t('cart.openPhoto')}
                            onClick={() => setZoom({ src, caption: item.description || item.code || '' })}
                          >
                            <img
                              src={src}
                              alt=""
                              loading="lazy"
                              width="56"
                              height="56"
                              onError={(e) => e.currentTarget.parentElement.classList.add('is-broken')}
                            />
                          </button>
                        );
                      })()}

                      <div className="cl-art">
                        <div className="cl-code-line">
                          <input
                            type="text"
                            className={`cl-inline cl-code${err.code ? ' cl-err' : ''}`}
                            value={item.code || ''}
                            onChange={(e) => onUpdate(item.id, 'code', e.target.value)}
                            placeholder={isAdj ? t('cart.adjustmentCode') : t('cart.code')}
                            aria-label={t('cart.code')}
                            disabled={isAdj}
                            readOnly={!canEditDiscount} // codice bloccato per chi non è abilitato ai preventivi
                            size={Math.max(10, String(item.code || '').length + 1)}
                          />
                          {isAdj && <span className="cl-tag">{t('cart.adjustmentTag')}</span>}
                        </div>
                        <textarea
                          className={`cl-inline cl-desc${err.description ? ' cl-err' : ''}`}
                          value={item.description || ''}
                          onChange={(e) => onUpdate(item.id, 'description', e.target.value)}
                          placeholder={isAdj ? t('cart.adjustmentDescription') : t('cart.description')}
                          aria-label={t('cart.description')}
                          title={item.description || undefined}
                          readOnly={!canEditDiscount} // descrizione bloccata per chi non è abilitato ai preventivi
                          rows={2}
                        />
                        {showNote && (
                          <input
                            type="text"
                            className="cl-inline cl-note"
                            value={item.notes || ''}
                            onChange={(e) => onUpdateNotes(item.id, e.target.value)}
                            placeholder={t('cart.rowNotesPlaceholder')}
                            aria-label={t('cart.rowNotes')}
                            autoFocus={!!noteOpen[item.id] && !item.notes}
                          />
                        )}
                        {(err.code || err.description || err.price || err.quantity) && (
                          <div className="cl-err-msg">{err.code || err.description || err.price || err.quantity}</div>
                        )}
                      </div>

                      {isAdj ? (
                        <span className="cl-qty-fixed cl-num">1</span>
                      ) : (
                        <div className={`cl-qty${err.quantity ? ' cl-err' : ''}`}>
                          <button
                            type="button"
                            aria-label={t('cart.decreaseQty')}
                            onClick={() => onQuantity(item.id, Number(item.quantity || 1) - 1)}
                            disabled={Number(item.quantity || 1) <= 1}
                          >
                            −
                          </button>
                          <input
                            type="number"
                            min="1"
                            value={Number(item.quantity || 0)}
                            onChange={(e) => onQuantity(item.id, e.target.value)}
                            aria-label={t('cart.quantity')}
                          />
                          <button type="button" aria-label={t('cart.increaseQty')} onClick={() => onQuantity(item.id, Number(item.quantity || 0) + 1)}>
                            +
                          </button>
                        </div>
                      )}

                      <label className="cl-cell cl-cell-list">
                      <span className="cl-cap">{t('cart.listPrice')}</span>
                      {canEditDiscount ? (
                      <input
                        type="number"
                        className={`cl-inline cl-price${err.price ? ' cl-err' : ''}`}
                        value={Number(item.unitPrice || 0)}
                        onChange={(e) => onPrice(item.id, e.target.value)}
                        min={isAdj ? undefined : 0}
                        step="0.01"
                        aria-label={t('cart.listPrice')}
                      />
                      ) : (
                        <span className="cl-num cl-price-ro">{fmtEur(Number(item.unitPrice || 0))}</span>
                      )}
                      </label>

                      <label className="cl-cell cl-cell-disc">
                      <span className="cl-cap">Sc%</span>
                      {isAdj ? (
                        <span className="cl-num cl-muted">0%</span>
                      ) : canEditDiscount ? (
                        <input
                          type="number"
                          className="cl-disc"
                          value={getRowDiscount(item)}
                          onChange={(e) => onDiscount(item.id, e.target.value)}
                          min="0"
                          max="100"
                          step="0.5"
                          aria-label={t('cart.discount')}
                        />
                      ) : (
                        <span className="cl-num">{getRowDiscount(item)}%</span>
                      )}
                      </label>

                      <span className="cl-cell cl-cell-net">
                        <span className="cl-cap">{t('cart.netPrice')}</span>
                        <span className="cl-num">{fmtEur(getNetUnit(item))}</span>
                      </span>
                      <span className="cl-cell cl-cell-total">
                        <span className="cl-cap">{t('cart.total')}</span>
                        <span className="cl-num cl-total">{fmtEur(getRowTotal(item))}</span>
                      </span>

                      <RowMenu label={t('cart.rowActions')}>
                        <button type="button" onClick={() => setNoteOpen((n) => ({ ...n, [item.id]: true }))}>
                          {item.notes ? t('cart.editRowNote') : t('cart.addRowNote')}
                        </button>
                        {onEdit && !isAdj && (isEditableProfile(item) || isCatalogEditable(item)) && (
                          <button type="button" onClick={() => onEdit(item)}>{t('cart.editRow')}</button>
                        )}
                        {!isAdj && <button type="button" onClick={() => onDuplicate(item)}>{t('cart.duplicateRow')}</button>}
                        <button type="button" disabled={!canUp} onClick={() => onMove(item.id, -1)}>{t('cart.moveUp')}</button>
                        <button type="button" disabled={!canDown} onClick={() => onMove(item.id, 1)}>{t('cart.moveDown')}</button>
                        <div className="cl-menu-sep" />
                        <div className="cl-menu-label">{t('cart.moveToZone')}</div>
                        {otherZones.map((z) => {
                          const c = zoneColorOf(z);
                          return (
                            <button type="button" key={z} onClick={() => onUpdate(item.id, 'zone', z)} title={z}>
                              <span className="cl-dot" style={{ background: c ? c.accent : 'var(--cl-line)' }} />
                              <span className="cl-menu-text">{z}</span>
                            </button>
                          );
                        })}
                        <button
                          type="button"
                          onClick={() => askZoneName((name) => onUpdate(item.id, 'zone', name))}
                        >
                          + {t('cart.newZone')}…
                        </button>
                        {normalizeZone(item.zone) && (
                          <button type="button" onClick={() => onUpdate(item.id, 'zone', '')}>{t('cart.noZone')}</button>
                        )}
                        <div className="cl-menu-sep" />
                        <button type="button" className="cl-danger" onClick={() => onRemove(item.id)}>{t('cart.remove')}</button>
                      </RowMenu>
                    </div>
                  );
                })}
                {q && !visible.length && <div className="cl-empty">{t('cart.noRowsMatch')}</div>}
              </div>
            )}

            {!isCollapsed && canEditDiscount && (
              <div className="cl-zone-foot">
                <button type="button" className="cl-add" onClick={() => onAddRow(group.zone)}>
                  + {group.zone ? `${t('cart.addRowIn')} ${group.zone}` : t('cart.addRow')}
                </button>
              </div>
            )}
          </section>
        );
      })}

      {zoom && (
        <div className="cl-zoom" role="dialog" aria-modal="true" aria-label={t('cart.openPhoto')} onClick={() => setZoom(null)}>
          <figure onClick={(e) => e.stopPropagation()}>
            <img src={zoom.src} alt={zoom.caption} />
            <figcaption>{zoom.caption}</figcaption>
          </figure>
          <button type="button" className="cl-zoom-close" aria-label={t('cart.close')} onClick={() => setZoom(null)}>×</button>
        </div>
      )}
    </div>
  );
};

export default CartLines;
